<template>
    <v-app>
        <v-app-bar app flat>
            <v-btn icon="mdi-arrow-left" variant="text" @click="router.push('/dashboard')" />
            <v-app-bar-title class="font-weight-medium">Profile</v-app-bar-title>
            <v-spacer></v-spacer>
            <v-chip variant="outlined" prepend-icon="mdi-account-circle" class="mr-2" density="comfortable">
                {{ email }}
            </v-chip>
            <v-menu offset-y>
                <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" text>
                        Menu
                        <v-icon end>mdi-menu-down</v-icon>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item title="Logout" value="Logout" @click="onLogout" density="compact" prepend-icon="mdi-logout"/>
                </v-list>
            </v-menu>
        </v-app-bar>

        <v-main>
            <v-container class="py-10">
                <v-row>
                    <v-col cols="12" md="8">
                        <v-card>
                            <v-card-title class="text-h6">{{ email }}</v-card-title>
                            <v-card-text v-if="checkingAdmin">
                                Loading...
                            </v-card-text>
                            <v-card-text v-else-if="!isAdmin">
                                You're signed in. You don't have rights to manage project access.
                            </v-card-text>
                        </v-card>

                        <v-card v-if="isAdmin" class="mt-4" :loading="projectsLoading">
                            <v-card-title class="text-subtitle-1">Project access</v-card-title>
                            <v-card-text>
                                <v-alert v-if="!projectsLoading && projects.length === 0" type="info" density="compact" variant="tonal">
                                    No projects found.
                                </v-alert>
                                <v-row v-for="project in projects" :key="project.id" class="align-center">
                                    <v-col cols="12" md="4">
                                        <div class="text-subtitle-2">{{ project.meta?.name || project.id }}</div>
                                        <div class="text-caption text-medium-emphasis">{{ project.id }}</div>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field
                                                v-model="draftWhiteLists[project.id]"
                                                label="Whitelisted emails (comma-separated)"
                                                density="comfortable"
                                                hide-details
                                                ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="2">
                                        <v-btn
                                                block
                                                color="primary"
                                                variant="tonal"
                                                :loading="saving[project.id]"
                                                @click="saveWhiteList(project.id)"
                                                >
                                                Save
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
            <v-snackbar v-model="snackbar" :timeout="3000" :color="statusColor">
                {{ statusMessage }}
            </v-snackbar>
        </v-main>
    </v-app>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { useFirestore, useFirebaseAuth } from 'vuefire';
import { collection, doc, getDocs, updateDoc } from '@firebase/firestore';
import { isAdminEmail, onLogout } from '/components/ttUtils';
import router from '../router';

interface ProjectRow {
    id: string;
    meta?: { name?: string; whiteList?: string[] };
}

const auth = useFirebaseAuth();
const email = (auth?.currentUser?.email || '').toLowerCase();
const db = useFirestore();

const isAdmin = ref(false);
const checkingAdmin = ref(true);
const projects = ref<ProjectRow[]>([]);
const projectsLoading = ref(false);
const draftWhiteLists = reactive<Record<string, string>>({});
const saving = reactive<Record<string, boolean>>({});
const snackbar = ref(false);
const statusMessage = ref('');
const statusColor = ref<'success' | 'error'>('success');

function parseEmails(value: string): string[] {
    return Array.from(new Set(
        value.split(',').map((entry) => entry.trim().toLowerCase()).filter(Boolean),
    ));
}

async function checkAdmin() {
    try {
        isAdmin.value = await isAdminEmail(db, email);
    } catch (error) {
        console.error('[checkAdmin] failed to read config/admins:', error);
        isAdmin.value = false;
    } finally {
        checkingAdmin.value = false;
    }
}

async function loadProjects() {
    if (!isAdmin.value) return;
    projectsLoading.value = true;
    try {
        const snapshot = await getDocs(collection(db, 'projects'));
        projects.value = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as ProjectRow));
        for (const project of projects.value) {
            draftWhiteLists[project.id] = Array.isArray(project.meta?.whiteList)
                ? project.meta!.whiteList!.join(', ')
                : '';
        }
    } catch (error: any) {
        statusMessage.value = `Failed to load projects: ${error.message || error}`;
        statusColor.value = 'error';
        snackbar.value = true;
    } finally {
        projectsLoading.value = false;
    }
}

async function saveWhiteList(projectId: string) {
    saving[projectId] = true;
    try {
        const whiteList = parseEmails(draftWhiteLists[projectId] || '');
        await updateDoc(doc(db, 'projects', projectId), { 'meta.whiteList': whiteList });
        draftWhiteLists[projectId] = whiteList.join(', ');
        statusMessage.value = `Updated access for ${projectId}.`;
        statusColor.value = 'success';
        snackbar.value = true;
    } catch (error: any) {
        statusMessage.value = `Failed to update ${projectId}: ${error.message || error}`;
        statusColor.value = 'error';
        snackbar.value = true;
    } finally {
        saving[projectId] = false;
    }
}

onMounted(async () => {
    await checkAdmin();
    await loadProjects();
});
</script>

<style scoped>
</style>
